// Componente de presentación que gestiona la sección de información básica del pedido.
import { useState, forwardRef, useImperativeHandle } from 'react';
import { Label } from '@shared/components/ui/label';
import { Input } from '@shared/components/ui/input';
import { Combobox } from '@shared/components/ui/AdvancedFilter/components';
import { useTranslation } from 'react-i18next';
import { useStores, usePaymentMethods } from '../../hooks';

interface OrderInformationProps {
  isLocked?: boolean;
  tenantChildId?: number;
}

export interface OrderInformationRef {
  getFormData: () => {
    reference: string;
    store: string;
    paymentMethod: string;
  };
  setFormData: (data: { reference: string; store: string; paymentMethod: string }) => void;
}

export const OrderInformation = forwardRef<OrderInformationRef>(
  ({ isLocked = false, tenantChildId }: OrderInformationProps, ref) => {
    const { t } = useTranslation('createEditOrdersModule');
    const { stores, loading: storesLoading } = useStores({
      tenantChildId,
      enabled: !isLocked,
    });
    const { paymentMethods, loading: paymentMethodsLoading } = usePaymentMethods();

    const [formData, setFormData] = useState({
      reference: '',
      store: '',
      paymentMethod: '',
    });

    const handleInputChange = (field: string, value: string) => {
      setFormData((prev) => ({
        ...prev,
        [field]: value,
      }));
    };

    const handleStoreChange = (value: string) => {
      handleInputChange('store', value);
    };

    const handlePaymentMethodChange = (value: string) => {
      handleInputChange('paymentMethod', value);
    };

    // Convertir los datos de la API a opciones para los comboboxes
    const tiendaOptions = stores.map((store) => ({
      label: store.name,
      value: store.id,
    }));

    const paymentMethodOptions = paymentMethods.map((method) => ({
      label: method.name,
      value: method.id,
    }));

    // Exponer los datos del formulario al componente padre
    useImperativeHandle(ref, () => ({
      getFormData: () => formData,
      setFormData: (data) => setFormData(data),
    }));

    return (
      <div className="p-4 space-y-4 mb-6 pb-6 gap-4 grid grid-cols-1 md:grid-cols-5 border-b border-card-border">
        <div className="col-span-1 md:col-span-2">
          <h2 className="text-lg font-bold">{t('orderInformation.title')}</h2>
        </div>
        <div className="col-span-1 md:col-span-3 !mt-0">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* Referencia */}
            <div className="space-y-2">
              <Label htmlFor="reference">{t('orderInformation.fields.reference.label')}</Label>
              <Input
                id="reference"
                type="text"
                placeholder={t('orderInformation.fields.reference.placeholder')}
                value={formData.reference}
                onChange={(e) => handleInputChange('reference', e.target.value)}
                maxLength={250}
                disabled={isLocked}
                data-testid="order-reference-input"
              />
            </div>

            {/* Tienda */}
            <div className="space-y-2">
              <Label htmlFor="store">{t('orderInformation.fields.store.label')}</Label>
              <Combobox
                options={tiendaOptions}
                placeholder={t('orderInformation.fields.store.placeholder')}
                value={formData.store}
                setValue={handleStoreChange}
                className="w-full"
                disabled={storesLoading || isLocked}
                dataTestId="order-store-combobox"
              />
            </div>

            {/* Método de pago */}
            <div className="space-y-2">
              <Label htmlFor="paymentMethod">{t('orderInformation.fields.paymentMethod.label')}</Label>
              <Combobox
                options={paymentMethodOptions}
                placeholder={t('orderInformation.fields.paymentMethod.placeholder')}
                value={formData.paymentMethod}
                setValue={handlePaymentMethodChange}
                className="w-full"
                disabled={paymentMethodsLoading || isLocked}
                dataTestId="order-payment-combobox"
              />
            </div>
          </div>
        </div>
      </div>
    );
  },
);
